import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim() });
      setDone(true);
      setEmail("");
      toast.success("You are with Aevum.", {
        description: "Welcome to endless time.",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail)
          ? "Please enter a valid email."
          : "Something went wrong. Please try again.");
      const message =
        err?.response?.status === 409
          ? "This email already belongs to Aevum."
          : err?.response?.status === 422
          ? "Please enter a valid email address."
          : detail;
      toast.error("Could not subscribe", { description: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative py-32 sm:py-40 border-t border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-end">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="md:col-span-6"
          >
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#155E63] font-bold mb-6">
              Chapter 02 — The Letter
            </p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-[#F5F2EA]">
              Join the
              <br />
              <span className="italic text-[#F5F2EA]/70">long hour.</span>
            </h2>
            <p className="mt-6 max-w-md text-[#A3A3A3] leading-relaxed font-light">
              Quiet dispatches. New garments first. Field notes from athletes
              who are still becoming. No noise — only signal.
            </p>
          </motion.div>

          {/* Right - form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-6"
          >
            {!done ? (
              <form onSubmit={onSubmit} data-testid="newsletter-form">
                <label
                  htmlFor="newsletter-email"
                  className="block text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-4"
                >
                  Your email
                </label>
                <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                  <input
                    id="newsletter-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@endless.time"
                    data-testid="newsletter-input"
                    className="flex-1 bg-transparent border-b border-white/20 focus:border-[#155E63] outline-none px-1 py-4 text-lg text-[#F5F2EA] placeholder:text-[#A3A3A3]/60 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="newsletter-submit-button"
                    className="group inline-flex items-center justify-center gap-3 bg-[#F5F2EA] text-[#050505] hover:bg-[#155E63] hover:text-[#F5F2EA] transition-all duration-500 px-8 py-4 text-[11px] tracking-[0.3em] uppercase font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Sending
                      </>
                    ) : (
                      <>
                        Subscribe
                        <ArrowRight
                          size={14}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </>
                    )}
                  </button>
                </div>
                <p className="mt-4 text-xs text-[#A3A3A3]/70 leading-relaxed">
                  By subscribing you agree to our quiet, infrequent letters. You
                  can leave any time.
                </p>
              </form>
            ) : (
              <div
                data-testid="newsletter-success"
                className="border border-[#155E63]/40 bg-[#0a100d] p-8"
              >
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#155E63] font-bold mb-4">
                  You are in
                </p>
                <p className="font-display text-3xl text-[#F5F2EA] leading-tight">
                  Welcome to <span className="italic">endless time.</span>
                </p>
                <p className="mt-3 text-sm text-[#A3A3A3]">
                  The next chapter will arrive when it is ready.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
